/**
 * Rising-sign confidence — score ALL twelve rising signs, then judge whether the evidence
 * actually picks one or is ambiguous between several.
 *
 * The trap this closes: the matcher silently returns its single top time even when two or three
 * rising signs fit nearly as well (Rahul: Taurus 83 / Aquarius 77 / Aries 66 from two events).
 * A systematic rectifier must instead SAY when it is not sure and which signs are still live, so
 * the user can add the evidence that separates them rather than trusting a confident-looking guess.
 *
 * `scan_rising_signs` coarsely sweeps the user's search window, scoring each sampled minute with
 * the same deterministic engine the matcher uses, and keeps the best EventFit per rising sign.
 * `confidence_verdict` then reports the leader, the contenders within a margin, and whether the
 * lead is decisive.
 */

import { calculate_chart, local_to_jd } from "../astro/chart";
import { birth_offset_at } from "./timezones";
import { get_config } from "../astro/config";
import { get_current_dasha, init_dasha_jd } from "../astro/dasha";
import { dasha_lord_at_level } from "../astro/models";
import { net_significator_strength, whole_sign_net_significator_strength } from "../astro/kp";
import { TIME_PRIOR_TIERS, parse_time_seconds, time_prior_for_window } from "./time_prior";
import { category_division, score_divisional_for_event } from "./divisional_scoring";
import {
  CATEGORY_KARAKAS,
  event_house_set,
  event_contra_houses,
} from "./kp_scoring";
import { era_pseudo_events } from "./eras";
import { sudarshana_lens_for_sign } from "./sudarshana";
import type { LifeEvent } from "./event_model";
import {
  birth_data_for_scoring,
  candidate_seconds,
  event_payload_to_life_event,
  is_multiday_search,
  score_candidate_record,
  search_span_seconds,
  search_start_date,
  seconds_to_candidate,
  time_label_to_tuple,
} from "./scoring";
import type { CandidateTuple } from "./scoring";
import { combined_trait_answers, influence_factor, per_question_sign_match, personality_match_for_sign, rising_sign_from_traits } from "./traits";
import { CATEGORY_TIMING_RELIABILITY } from "./kp_scoring";
import { addDays, formatISODate, parseISODate, parseISODateTime, plainDateTime, diffSeconds } from "./datetime";
import type { PlainDate, PlainDateTime } from "./datetime";
import { _py_float, _truthy, pythonRound } from "./pyutil";

// How decisively the leader must beat the next sign (EventFit points) to count as confident.
export const CONFIDENT_MARGIN = 8.0;
// Signs within this many points of the leader are still "live" and worth disambiguating.
export const CONTENDER_MARGIN = 8.0;
// Confidence requires not just a score lead but ENOUGH evidence. A handful of events can fit the
// wrong rising sign better than the right one (a benefic stellium landing in an event house on the
// wrong ascendant), so below this many well-dated events the rising sign is never declared certain
// however large the lead — the system disambiguates instead of committing to a confident guess.
export const MIN_EVENTS_FOR_CONFIDENCE = 4;
// When evidence is too thin to be confident, verify the leader against this many top signs.
export const THIN_EVIDENCE_CONTENDERS = 3;
// Coarse sweep step. A rising sign occupies ~2h, so a few minutes resolves which sign is best
// without paying for a full per-second scan (that is the matcher's job once the sign is fixed).
export const DEFAULT_STEP_MINUTES = 4;

// _truthy / _py_float are the shared Python-semantics helpers from ./pyutil.

export function _seconds_to_label(seconds: number): string {
  seconds = ((seconds % (24 * 3600)) + 24 * 3600) % (24 * 3600);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(seconds / 3600))}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
}

/**
 * Given second-of-day marks where a sign rises, return the contiguous (start, end) arc,
 * correctly handling a sign whose rising window wraps past midnight (e.g. 22:30 → 00:30). The
 * arc is everything except the single largest gap between consecutive marks on the 24h circle.
 */
export function _circular_arc(marks: number[]): [number, number] | null {
  const uniq = [...new Set(marks.map((m) => ((m % (24 * 3600)) + 24 * 3600) % (24 * 3600)))].sort((a, b) => a - b);
  if (!uniq.length) {
    return null;
  }
  if (uniq.length === 1) {
    return [uniq[0], uniq[0]];
  }
  const n = uniq.length;
  let max_gap = -1;
  let gap_index = 0;
  for (let i = 0; i < n; i++) {
    const gap = (((uniq[(i + 1) % n] - uniq[i]) % (24 * 3600)) + 24 * 3600) % (24 * 3600);
    if (gap > max_gap) {
      max_gap = gap;
      gap_index = i;
    }
  }
  return [uniq[(gap_index + 1) % n], uniq[gap_index]]; // start just after the big gap, end just before it
}

export function _window_second_range(search: Record<string, any>): [number, number] {
  const start = candidate_seconds(time_label_to_tuple(search["start"]));
  let end = candidate_seconds(time_label_to_tuple(search["end"]));
  if (end < start) {
    // overnight window (e.g. 22:00 -> 04:00) wraps past midnight
    end += 24 * 3600;
  }
  return [start, end];
}

/**
 * Coarsely sweep the search window and return the best-fitting candidate per rising sign.
 *
 * Returns {sign_name: {best_time, event_fit, rising_sign_fit, deterministic}} for every sign
 * that actually rises somewhere in the window.
 */
export function scan_rising_signs(
  user_profile: Record<string, any>,
  step_minutes: number = DEFAULT_STEP_MINUTES,
): Record<string, Record<string, any>> {
  const search = _truthy(user_profile["search"]) ? user_profile["search"] : {};
  if (!_truthy(search["start"]) || !_truthy(search["end"])) {
    return {};
  }
  const people_key = user_profile["slug"];
  const birth_data = birth_data_for_scoring(user_profile);
  // Real dated events PLUS any answered life-phase ("era") pseudo-events — the era answers carry the
  // dasha-boundary signal into the same events ranking. No-op when no eras are answered.
  const events = [
    ...(_truthy(user_profile["events"]) ? user_profile["events"] : []),
    ...era_pseudo_events(user_profile),
  ];
  // Includes rejected traits under the reserved key, so the scorers apply the absolute gates.
  const traits = combined_trait_answers(user_profile);
  const periods = _truthy(user_profile["life_periods"]) ? user_profile["life_periods"] : {};
  const timezone_offset = _py_float(user_profile["birth"]["timezone_offset"]);
  const life_events: LifeEvent[] = [];
  for (const event of events) {
    try {
      life_events.push(event_payload_to_life_event(event));
    } catch (exc) {
      if (exc instanceof TypeError) throw exc;
      continue;
    }
  }

  // Multi-day windows: `_window_second_range` would collapse them (00:00 → 00:00 over 3 days reads
  // as [0, 0] — a one-second sweep), so mirror `rising_sign_breakdown`'s dated sweep instead:
  // absolute seconds across the whole span, each sampled minute scored on its ACTUAL calendar day.
  const multiday = is_multiday_search(search);
  const sweep_start = multiday ? candidate_seconds(time_label_to_tuple(search["start"])) : 0;
  const [start_s, end_s] = multiday
    ? [sweep_start, sweep_start + search_span_seconds(search)]
    : _window_second_range(search);
  const win_start_date: PlainDate | null = multiday ? search_start_date(search) : null;
  const step = Math.max(60, Math.trunc(step_minutes) * 60);
  const best: Record<string, Record<string, any>> = {};
  let second = start_s;
  while (second <= end_s) {
    const wrapped = second % (24 * 3600);
    const candidate: CandidateTuple = [Math.floor(wrapped / 3600), Math.floor((wrapped % 3600) / 60), wrapped % 60];
    const candidate_date: PlainDate | null =
      multiday && win_start_date ? addDays(win_start_date, Math.floor(second / (24 * 3600))) : null;
    const record = score_candidate_record(
      people_key, candidate, birth_data, life_events, events, timezone_offset, {},
      "rising_sign_scan", null, search, traits, periods, null, candidate_date,
    );
    const sign = record["ascendant_sign"];
    // Rank signs by the combined deterministic score (EventFit + the adaptive rising-sign
    // bias), so observed appearance/temperament actually moves this table — entering a person's
    // real features visibly lifts the matching sign, instead of the table ignoring them.
    let rank_value = record["deterministic_score"];
    if (rank_value === null || rank_value === undefined) {
      rank_value = record["event_fit_score"];
    }
    if (rank_value === null || rank_value === undefined) {
      rank_value = record["rising_sign_fit_score"];
    }
    if (sign !== null && sign !== undefined && rank_value !== null && rank_value !== undefined) {
      const prior = best[sign];
      if (prior === undefined || rank_value > prior["rank_value"]) {
        best[sign] = {
          best_time: record["time"],
          rank_value: Number(rank_value),
          event_fit: record["event_fit_score"],
          rising_sign_fit: record["rising_sign_fit_score"],
          deterministic: record["deterministic_score"],
        };
      }
    }
    second += step;
  }
  return best;
}

/**
 * Given per-sign best scores and how much dated evidence backs them, report the leader, the
 * still-live contenders, and whether the verdict is genuinely confident — which needs BOTH a
 * decisive score lead AND enough events.
 */
export function confidence_verdict(
  per_sign: Record<string, Record<string, any>>,
  event_count = 0,
  confident_margin: number = CONFIDENT_MARGIN,
  contender_margin: number = CONTENDER_MARGIN,
): Record<string, any> {
  const ranked = Object.entries(per_sign).sort((a, b) => b[1]["rank_value"] - a[1]["rank_value"]);
  if (!ranked.length) {
    return { available: false, confident: false, leader: null, contenders: [], ranked: [] };
  }
  const [leader_sign, leader] = ranked[0];
  const second_score = ranked.length > 1 ? ranked[1][1]["rank_value"] : 0.0;
  const separation = leader["rank_value"] - second_score;

  const entry = (item: [string, Record<string, any>]): Record<string, any> => {
    const [s, v] = item;
    return { sign: s, score: pythonRound(v["rank_value"], 1), best_time: v["best_time"] };
  };

  const separation_ok = ranked.length === 1 || separation > confident_margin;
  const evidence_ok = event_count >= MIN_EVENTS_FOR_CONFIDENCE;
  const confident = Boolean(separation_ok && evidence_ok);

  let contenders: Array<Record<string, any>>;
  let reason: string;
  if (confident) {
    contenders = [entry(ranked[0])];
    reason = "decisive lead with enough events";
  } else {
    // Live signs by score proximity...
    let close = ranked.filter((item) => leader["rank_value"] - item[1]["rank_value"] <= contender_margin);
    // ...but when the evidence itself is too thin, the score lead is not trustworthy, so widen
    // to the top few signs to verify the leader against rather than trusting the gap.
    if (!evidence_ok) {
      close = ranked.slice(0, Math.max(THIN_EVIDENCE_CONTENDERS, close.length));
    }
    contenders = close.map(entry);
    if (!evidence_ok && !separation_ok) {
      reason = `only ${event_count} event(s) and the top signs score closely`;
    } else if (!evidence_ok) {
      reason = `only ${event_count} event(s) — a few events can fit the wrong rising sign`;
    } else {
      reason = "the top rising signs score too closely to choose";
    }
  }

  return {
    available: true,
    confident: confident,
    separation_ok: Boolean(separation_ok),
    evidence_ok: Boolean(evidence_ok),
    event_count: Math.trunc(event_count),
    reason: reason,
    leader: leader_sign,
    leader_time: leader["best_time"],
    separation: pythonRound(separation, 1),
    contenders: contenders,
    ranked: ranked.map(entry),
  };
}

/**
 * The clock interval on the birth day during which the given sign is on the Ascendant, taken
 * STRICTLY (no padding) so the constrained search can't leak into the neighbouring sign. Used to
 * narrow the search once a rising sign is chosen.
 */
export function sign_rising_window(
  user_profile: Record<string, any>,
  sign_name: string,
  step_minutes = 2,
): [string, string] | null {
  const birth = _truthy(user_profile["birth"]) ? user_profile["birth"] : {};
  let birth_date;
  let lat: number;
  let lon: number;
  let tz: number;
  try {
    if (!("date" in birth)) throw new Error("KeyError: 'date'");
    birth_date = parseISODate(birth["date"]);
    [lat, lon, tz] = [_py_float(birth["lat"]), _py_float(birth["lon"]), _py_float(birth["timezone_offset"])];
  } catch {
    return null;
  }
  const cfg = get_config();
  const step = Math.max(1, Math.trunc(step_minutes));
  const matches: number[] = [];
  for (let total = 0; total < 24 * 60; total += step) {
    const hh = Math.floor(total / 60);
    const mm = total % 60;
    const probe_dt = plainDateTime(birth_date.year, birth_date.month, birth_date.day, hh, mm, 0);
    // Offset at the scanned moment (or the user-confirmed fixed offset): the windows must agree
    // with the per-candidate offsets the scoring engine uses, incl. a pinned pre-1970 offset.
    const tz_at = birth_offset_at(
      { lat, lon, timezone_offset: tz, timezone_offset_fixed: birth["timezone_offset_fixed"] }, probe_dt, tz,
    );
    const chart = calculate_chart(probe_dt, lat, lon, tz_at, cfg);
    if (chart.planets["ascendant"].sign_name === sign_name) {
      matches.push(total * 60); // seconds
    }
  }
  const arc = _circular_arc(matches);
  if (arc === null) {
    return null;
  }
  // No padding: the window stays STRICTLY inside the chosen sign so the timing scan can't leak
  // into the neighbouring sign (whose events might outscore it at the boundary). At worst the
  // very edge ~1 step is trimmed, which never matters for a real birth time inside the sign.
  return [_seconds_to_label(arc[0]), _seconds_to_label(arc[1])];
}

// Karaka endorsement bonus, mirroring kp_scoring._KARAKA_NET_BONUS — a doc-named natural
// significator that is ALSO a chart significator is the strongest endorsement.
const _LENS_KARAKA_BONUS = 2.0;

/**
 * The three near-independent classical EVENT lenses for one rising-sign chart, each a single
 * number = how strongly the running dasha lords (maha + antar + a little pratyantar) signify the
 * event's houses, averaged over the dated events:
 *   • placidus  — signed significators by Placidus cusps (the KP house system);
 *   • wholesign — signed significators by whole-sign houses (the Parashari system);
 *   • divisional — the event's governing varga verdict (D9 marriage, D7 children, D10 career, …);
 *   • sudarshana — the DASHA-INDEPENDENT Sudarshana Chakra annual lens (src/lib/sudarshana.ts,
 *     source_learning/11_sudarshana_chakra.md): each event's (age mod 12)+1 year house judged
 *     benefic/malefic across the Asc/Moon/Sun circles vs the event's polarity. Null when the
 *     doc's skip rule fires for this chart (two of Sun/Moon/Asc share a sign).
 *
 * Placidus and whole-sign place the same planet in DIFFERENT bhavas, so they are decorrelated:
 * on the AA-rated test set each alone ranks the true rising sign only at chance, but z-combined
 * across the candidate signs they rank it in the top third. This is the engine's actual
 * rising-sign discriminator (see `_spread_event_fit_by_ensemble`).
 */
export function event_lens_scores(
  rep_dt: PlainDateTime,
  lat: number,
  lon: number,
  tz: number,
  events: Array<Record<string, any>>,
): { placidus: number; wholesign: number; divisional: number; sudarshana: number | null } | null {
  let chart;
  let dasha_init: Record<string, number>;
  try {
    chart = calculate_chart(rep_dt, lat, lon, tz, get_config());
    dasha_init = init_dasha_jd(chart, local_to_jd(rep_dt, tz));
  } catch {
    return null;
  }
  let pl = 0;
  let ws = 0;
  let dv = 0;
  let n = 0;
  let nd = 0;
  for (const event of events) {
    const category = String(event["start_date"] ? event["category"] || "" : "").trim().toLowerCase();
    // Valence-aware: a NEGATIVE-valence era answer ("this area went badly") scores against the
    // area's CONTRA houses, so "good" and "hard" answers in the same period point at different signs.
    // Real events carry no valence → event_house_set returns the primary houses (unchanged behaviour).
    const houses = event_house_set(event);
    if (!houses.length) {
      continue;
    }
    let event_dt;
    try {
      event_dt = parseISODateTime(event["start_date"]);
    } catch {
      continue;
    }
    if (diffSeconds(event_dt, rep_dt) <= 0) {
      continue;
    }
    // PER-EVENT WEIGHT — the sign-selection lens must honour the same event weighting the rest of
    // the engine uses, NOT count every event equally: the user's per-event INFLUENCE (minimal/low/
    // medium/high → 0.1/0.25/0.5/1.0), the category TIMING RELIABILITY (a fuzzy leisure trip times
    // far more loosely than a marriage/childbirth/death), and the event's confidence. Without this a
    // handful of weak, low-influence travel/relocation events out-vote the sharp life events and can
    // push the true sign down the list — and the user's influence dial would do nothing here.
    // The event's own `weight` factor matches the KP scorer's weighting (real events default to 1,
    // so nothing changes for them); era pseudo-events SPLIT one answer's weight across their
    // quartile samples (see eras.era_pseudo_events), and honouring it here keeps each answer's
    // total lens influence equal to the single-sample era it replaced.
    const w =
      Math.max(0, Number(event["weight"] ?? 1)) *
      influence_factor(event["influence"]) *
      (CATEGORY_TIMING_RELIABILITY[category] ?? 0.6) *
      Math.max(0, Math.min(1, Number(event["confidence"] ?? 1)));
    if (w <= 0) {
      continue;
    }
    const contra = event_contra_houses(event);
    const pnet = net_significator_strength(chart, houses, contra);
    const wnet = whole_sign_net_significator_strength(chart, houses, contra);
    for (const karaka of CATEGORY_KARAKAS[category] ?? []) {
      if (karaka in pnet) pnet[karaka] += _LENS_KARAKA_BONUS;
      if (karaka in wnet) wnet[karaka] += _LENS_KARAKA_BONUS;
    }
    let dasha;
    try {
      dasha = get_current_dasha(event_dt, rep_dt, dasha_init, tz);
    } catch {
      continue;
    }
    const ma = dasha_lord_at_level(dasha, "maha");
    const an = dasha_lord_at_level(dasha, "antar");
    const pr = dasha_lord_at_level(dasha, "pratyantar");
    // maha + antar co-equal (theme + activation), pratyantar a lighter refiner.
    const chain = (net: Record<string, number>) =>
      ((net[ma ?? ""] ?? 0) + (net[an ?? ""] ?? 0) + 0.6 * (net[pr ?? ""] ?? 0)) / 2.6;
    pl += w * chain(pnet);
    ws += w * chain(wnet);
    const division = category_division(category);
    if (division && division !== 1) {
      const [ds] = score_divisional_for_event(chart, division, houses, event_dt, rep_dt, dasha_init, tz);
      if (ds !== null) {
        dv += w * ds;
        nd += w;
      }
    }
    n += w;
  }
  if (!n) {
    return null;
  }
  // The Sudarshana annual lens weighs its events internally with the SAME per-event weighting as
  // above; null when the chakra's skip rule fires for this chart or nothing could be weighed.
  const sudarshana = sudarshana_lens_for_sign(chart, rep_dt, events);
  return { placidus: pl / n, wholesign: ws / n, divisional: nd ? dv / nd : 50, sudarshana };
}

/** z-score a list against its own mean/sd (sd floored to 1 so a flat list maps to all-zeros). */
function _zscores(values: number[]): number[] {
  const n = values.length;
  if (!n) return [];
  const mean = values.reduce((a, b) => a + b, 0) / n;
  const sd = Math.sqrt(values.reduce((a, b) => a + (b - mean) ** 2, 0) / n) || 1;
  return values.map((v) => (v - mean) / sd);
}

// How far (score points) one standard deviation of a single lens spreads the per-sign Events fit.
// Three lenses sum, so a sign that is ~+1σ on all three lifts ~3× this above the evidence level.
//
// HONEST-CONFIDENCE NOTE: this gain controls the DISPLAYED magnitude of separation, NOT the
// ranking order — order is fixed by the z-score sum (`ens_z`) regardless of gain. The three lenses
// (Placidus / whole-sign / divisional) are NOT statistically independent — they reuse the same
// planets, dashas, events and category→house maps — so a large gain manufactures a strong-looking
// lead out of what is often a tightly clustered, ambiguous band. Kept deliberately modest so the
// shown band reflects the true (usually small) evidence separation; it sharpens relative ordering
// without advertising confidence the evidence does not support.
const ENSEMBLE_SPREAD_GAIN = 2.0;
// Per-lens weights in the z-combination (see `_spread_event_fit_by_ensemble`). HONEST CALIBRATION
// (2026-06-20): only the PLACIDUS net significator (classical KP significators in the actual house
// system, ~constant across a sign's window) carries real sign-level signal. The whole-sign lens
// adds noise over it, and the DIVISIONAL lens is a TIME discriminator, not a SIGN one — its
// fast-moving varga lagna makes a per-sign reading near-random (a midpoint sample once ranked a
// recorded-time Aries dead-last, and AVERAGING it over the window collapses the references to
// chance — i.e. its apparent past "discrimination" was midpoint-sampling LUCK overfit to n=5). So
// both are zeroed. Measured blind: ~avg rank 3.0 / top-1 1-of-4 (vs ~6.5 chance) — modest, honest.
// Tunable seam; re-sweep `lib/tuning` as more ground-truth charts accrue.
//
// SUDARSHANA (2026-07-20, src/lib/sudarshana.ts — the dasha-INDEPENDENT annual lens): SHIPPED AT 0
// (computed for audit, same treatment as whole-sign) per the honest-calibration precedent. Measured
// on the accuracy harness (4 clean charts; baseline w=0: top-1 1/4, top-3 2/4, avg 3.50):
//   w=1.0  → top-1 1/4, top-3 2/4, avg 4.50  (clear regression — Obama 4→11, Diana 1→2)
//   w=0.5  → top-1 2/4, top-3 2/4, avg 3.75  (mixed — Jolie 3→1 but Obama 4→7)
//   w=0.25 → top-1 2/4, top-3 2/4, avg 3.50  (aggregates improve-or-hold, BUT the per-chart
//            direction is uncorrelated with truth: true-sign fit up on 2 charts (Jolie +0.7,
//            Arnold +0.3), down on 2 (Obama −0.9, Diana −0.2); the top-1 gain flips a 0.3-point
//            near-tie for Jolie while pushing Obama's true sign 4→6 — placement luck on n=4,
//            the same overfit signature that once made the divisional lens look 4× good.)
// So: weight 0 until more ground-truth charts show a consistent per-chart direction; the per-sign
// value stays visible in each entry's `event_lens.sudarshana` (null where the doc's skip rule
// fires — two of Sun/Moon/Asc sharing a sign for that candidate Ascendant).
export const ENSEMBLE_LENS_WEIGHTS = { placidus: 1.0, wholesign: 0.0, divisional: 0.0, sudarshana: 0.0 };
// The ensemble re-ranking only makes sense when several rising signs actually compete (a WIDE,
// sign-selection search). Below this it would just add noise to an already-chosen window.
const ENSEMBLE_MIN_SIGNS = 3;

/**
 * Re-rank the rising signs by a classical multi-lens ensemble (Placidus + whole-sign + divisional
 * significators), and rewrite each sign's `best_event_fit` to a value that is SPREAD by that
 * ensemble while staying anchored at the honest absolute evidence level.
 *
 * Why: the single-house-system Placidus fit ranks the true ascendant only at chance (measured),
 * because the engine's averaged components collapse every sign into a narrow band. Combining the
 * lenses by cross-sign z-score sharpens the relative ordering. CAVEAT: the lenses are correlated
 * (same planets / dashas / events / house maps), so this is NOT independent corroboration — the
 * `ENSEMBLE_SPREAD_GAIN` is kept modest so the band stays centred on the mean Placidus fit and
 * ambiguous evidence still reads as a near-tie (low/grey) rather than a manufactured strong lead.
 *
 * `entries` is mutated in place. No-op when fewer than `ENSEMBLE_MIN_SIGNS` signs compete (a narrow,
 * already-committed window) or the lenses can't be computed.
 */
export function _spread_event_fit_by_ensemble(
  entries: Array<Record<string, any>>,
  birth_date: { year: number; month: number; day: number },
  lat: number,
  lon: number,
  tz: number,
  events: Array<Record<string, any>>,
  tz_fixed = false,
): void {
  // birth_info shape understood by `birth_offset_at` (honours a user-confirmed fixed offset).
  const birth_info = { lat, lon, timezone_offset: tz, timezone_offset_fixed: tz_fixed };
  const ranked_signs = entries.filter((e) => e["best_event_fit"] !== null && e["best_event_fit"] !== undefined);
  if (ranked_signs.length < ENSEMBLE_MIN_SIGNS) {
    return;
  }
  const lenses: Array<{ entry: Record<string, any>; placidus: number; wholesign: number; divisional: number; sudarshana: number | null } | null> =
    ranked_signs.map((entry) => {
      // Representative chart at the MIDPOINT of the sign's rising window. The PLACIDUS net
      // significator (the only weighted lens — see ENSEMBLE_LENS_WEIGHTS) is ~constant across a
      // single sign's window, so the midpoint is a faithful sample for it. (The whole-sign and
      // DIVISIONAL lenses are still computed for audit/display but carry weight 0: whole-sign adds
      // no signal over placidus, and the divisional rides a fast-moving varga lagna — it swings 40+
      // points WITHIN a sign window, so it discriminates the exact TIME, not the SIGN, and as a
      // sign-selector it is pure noise.)
      const start = candidate_seconds(time_label_to_tuple(entry["window_start"]));
      let end = candidate_seconds(time_label_to_tuple(entry["window_end"]));
      if (end < start) end += 24 * 3600;
      const mid = Math.floor((start + end) / 2) % (24 * 3600);
      const rep_dt = plainDateTime(
        birth_date.year, birth_date.month, birth_date.day,
        Math.floor(mid / 3600), Math.floor((mid % 3600) / 60), mid % 60,
      );
      const lens = event_lens_scores(rep_dt, lat, lon, birth_offset_at(birth_info, rep_dt, tz), events);
      return lens ? { entry, ...lens } : null;
    });
  const ok = lenses.filter((l): l is NonNullable<typeof l> => l !== null);
  if (ok.length < ENSEMBLE_MIN_SIGNS) {
    return;
  }
  const zp = _zscores(ok.map((l) => l.placidus));
  const zw = _zscores(ok.map((l) => l.wholesign));
  const zd = _zscores(ok.map((l) => l.divisional));
  // Sudarshana lens: NULL for a sign whose chart trips the doc's skip rule (two of Sun/Moon/Asc
  // share a sign — a per-candidate-Ascendant condition, so some signs legitimately have no
  // reading). A null is parked AT THE MEAN of the computed values so its z-score is exactly 0 —
  // neutral, never rewarded or punished for being unreadable.
  const su_present = ok.map((l) => l.sudarshana).filter((v): v is number => v !== null && v !== undefined);
  const su_mean = su_present.length ? su_present.reduce((a, b) => a + b, 0) / su_present.length : 50;
  const zs = _zscores(ok.map((l) => l.sudarshana ?? su_mean));
  // Anchor the band at the mean of the ORIGINAL (placidus) per-sign fits, so the overall level
  // still reflects how strong the evidence is (weak evidence → low band → grey), and only the
  // RELATIVE ordering is sharpened by the ensemble.
  const level =
    ranked_signs.reduce((a, e) => a + Number(e["best_event_fit"]), 0) / ranked_signs.length;
  ok.forEach((l, i) => {
    // Weighted z-combination of the three lenses. Per ENSEMBLE_LENS_WEIGHTS, only the PLACIDUS lens
    // is weighted today (whole-sign and divisional are 0): the divisional swings within a sign's
    // window, so it discriminates the exact time, not the sign, and was found to be overfit noise as
    // a sign-selector. The weighted form is kept as the tuning seam should a genuinely stable
    // sign-level lens be added later.
    const ens_z = ENSEMBLE_LENS_WEIGHTS.placidus * zp[i]
      + ENSEMBLE_LENS_WEIGHTS.wholesign * zw[i]
      + ENSEMBLE_LENS_WEIGHTS.divisional * zd[i]
      + ENSEMBLE_LENS_WEIGHTS.sudarshana * zs[i];
    const spread = Math.max(0, Math.min(100, level + ENSEMBLE_SPREAD_GAIN * ens_z));
    l.entry["best_event_fit_placidus"] = l.entry["best_event_fit"]; // keep the raw lens for audit
    l.entry["event_lens"] = {
      placidus: pythonRound(l.placidus, 2),
      wholesign: pythonRound(l.wholesign, 2),
      divisional: pythonRound(l.divisional, 1),
      // Sudarshana annual verdict alignment (null = chakra skipped for this sign's chart).
      sudarshana: l.sudarshana === null || l.sudarshana === undefined ? null : pythonRound(l.sudarshana, 1),
    };
    l.entry["best_event_fit"] = pythonRound(spread, 1);
  });
  // A sign whose lens could NOT be computed must not keep its RAW Placidus fit sitting amid the
  // SPREAD values — the two scales are not commensurate, so the raw number would rank against
  // rescaled ones. Park it at the band's anchor level (the mean the spread is centred on) and flag
  // it (`lens_failed`) so the UI can present it as uncertain rather than genuinely ranked.
  lenses.forEach((l, i) => {
    if (l !== null) return;
    const entry = ranked_signs[i];
    entry["best_event_fit_placidus"] = entry["best_event_fit"]; // keep the raw fit for audit
    entry["lens_failed"] = true;
    entry["best_event_fit"] = pythonRound(Math.max(0, Math.min(100, level)), 1);
  });
}

// Where the Eras band sits when there are no real events to anchor it to (events anchor it at their
// own mean when present). A neutral mid-band level — the RELATIVE ordering is what the ensemble adds.
const ERA_FIT_ANCHOR = 62;

/**
 * Compute a SEPARATE per-sign "Eras" fit from the answered life-phase pseudo-events only, written to
 * `best_era_fit`. This is the same placidus-significator lens used for events (so it is valence-aware
 * — a "hard" era answer scores against the area's contra houses), but kept in its OWN metric so the
 * era signal is visible on the graph and folded into Derived rather than buried inside the Events
 * number. No-op when there are no era answers or fewer than `ENSEMBLE_MIN_SIGNS` signs compete.
 * `entries` is mutated in place. `anchor_level` (the events' mean fit, when present) keeps the era
 * band visually comparable to the events band.
 */
export function _spread_era_fit(
  entries: Array<Record<string, any>>,
  birth_date: { year: number; month: number; day: number },
  lat: number,
  lon: number,
  tz: number,
  era_events: Array<Record<string, any>>,
  tz_fixed = false,
  anchor_level?: number,
): void {
  if (!era_events.length) return;
  const ranked = entries.filter((e) => e["window_start"] != null && e["window_end"] != null);
  if (ranked.length < ENSEMBLE_MIN_SIGNS) return;
  const birth_info = { lat, lon, timezone_offset: tz, timezone_offset_fixed: tz_fixed };
  const lensed = ranked
    .map((entry) => {
      const start = candidate_seconds(time_label_to_tuple(entry["window_start"]));
      let end = candidate_seconds(time_label_to_tuple(entry["window_end"]));
      if (end < start) end += 24 * 3600;
      const mid = Math.floor((start + end) / 2) % (24 * 3600);
      const rep_dt = plainDateTime(
        birth_date.year, birth_date.month, birth_date.day,
        Math.floor(mid / 3600), Math.floor((mid % 3600) / 60), mid % 60,
      );
      const lens = event_lens_scores(rep_dt, lat, lon, birth_offset_at(birth_info, rep_dt, tz), era_events);
      return lens ? { entry, placidus: lens.placidus } : null;
    })
    .filter((l): l is { entry: Record<string, any>; placidus: number } => l !== null);
  if (lensed.length < ENSEMBLE_MIN_SIGNS) return;
  const z = _zscores(lensed.map((l) => l.placidus));
  const level = anchor_level ?? ERA_FIT_ANCHOR;
  lensed.forEach((l, i) => {
    l.entry["best_era_fit"] = pythonRound(Math.max(0, Math.min(100, level + ENSEMBLE_SPREAD_GAIN * z[i])), 1);
  });
}

/**
 * Detect whether the dated events are CONCENTRATED in a single mahadasha (life-period). Several
 * events inside one mahadasha hinge on the SAME planet's placement, so they confirm largely one
 * thing — the sign estimate from them is weak no matter how many events there are. The mahadasha at
 * an event is ascendant-invariant (it depends on the birth Moon, not the rising sign), so one
 * representative chart suffices. Returns null when there are too few events to judge (<3).
 */
export function events_dasha_concentration(
  user_profile: Record<string, any>,
): { total: number; distinct: number; dominant_lord: string; dominant_count: number; concentrated: boolean } | null {
  const events = _truthy(user_profile["events"]) ? user_profile["events"] : [];
  const birth = _truthy(user_profile["birth"]) ? user_profile["birth"] : {};
  if (events.length < 3 || !("date" in birth)) return null;
  const tz = _py_float(birth["timezone_offset"]);
  let dasha_init: Record<string, number>;
  let rep: PlainDateTime;
  try {
    const bd = parseISODate(birth["date"]);
    rep = plainDateTime(bd.year, bd.month, bd.day, 12, 0, 0);
    const lat = _py_float(birth["lat"]);
    const lon = _py_float(birth["lon"]);
    // Birth-side offset resolved the same way the scorer does (honours a user-confirmed fixed
    // offset / pinned pre-1970 offsets), so the reference dasha timeline matches the engine's.
    const tz_at = birth_offset_at(
      { lat, lon, timezone_offset: tz, timezone_offset_fixed: birth["timezone_offset_fixed"] }, rep, tz,
    );
    const chart = calculate_chart(rep, lat, lon, tz_at, get_config());
    dasha_init = init_dasha_jd(chart, local_to_jd(rep, tz_at));
  } catch {
    return null;
  }
  const counts: Record<string, number> = {};
  let total = 0;
  for (const ev of events) {
    if (!ev["start_date"]) continue;
    let edt: PlainDateTime;
    try {
      edt = parseISODateTime(ev["start_date"]);
    } catch {
      continue;
    }
    if (diffSeconds(edt, rep) <= 0) continue;
    let lord: string | null | undefined;
    try {
      lord = dasha_lord_at_level(get_current_dasha(edt, rep, dasha_init, tz), "maha");
    } catch {
      continue;
    }
    if (!lord) continue;
    counts[lord] = (counts[lord] ?? 0) + 1;
    total += 1;
  }
  if (total < 3) return null;
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  const [dominant_lord, dominant_count] = sorted[0];
  // Concentrated when one mahadasha holds the majority (≥60%) of the dated events.
  return { total, distinct: sorted.length, dominant_lord, dominant_count, concentrated: dominant_count / total >= 0.6 };
}

/**
 * First-cycle data: break the search range into its rising signs and, for each sign, report
 * the HIGHEST match of every event and the appearance match — so the user can choose the sign.
 * Signs are returned in ASCENDING-BY-TIME order (chronological through the day). The appearance
 * match uses the AI-estimated `appearance_likelihood` when given, else the deterministic fallback.
 *
 * One coarse pass over the range (signs change every ~2h; per-second resolution is pointless
 * here), grouped by ascendant sign. Per sign: its clock window within the range, the best score
 * each individual event reaches anywhere in that window (from the per-event KP details), the best
 * combined time, and the overall appearance/temperament match. Per-trait detail comes from
 * per_question_sign_match(). Returns {available, event_labels, signs:[...], traits:[...]}.
 */
// "Explained events" now lives in scoring.ts (every candidate record carries it — Level-1 drill
// and the Compare page read it off the record); re-exported here for the Level-0 consumers/tests.
export { explained_events_at } from "./scoring";

export function rising_sign_breakdown(
  user_profile: Record<string, any>,
  step_minutes = 3,
  appearance_likelihood: Record<string, number> | null = null,
): Record<string, any> {
  const search = _truthy(user_profile["search"]) ? user_profile["search"] : {};
  if (!(_truthy(search["start"]) && _truthy(search["end"]))) {
    return { available: false, reason: "No search window." };
  }
  const people_key = user_profile["slug"];
  const birth_data = birth_data_for_scoring(user_profile);
  // Real dated events drive the Events metric; the answered life-phase ("era") pseudo-events are kept
  // SEPARATE (their own `best_era_fit` metric, computed below) so the era signal is visible on the
  // graph and folded into Derived rather than diluted into — and hidden inside — the Events number.
  const events = _truthy(user_profile["events"]) ? user_profile["events"] : [];
  const era_events = era_pseudo_events(user_profile);
  // Includes rejected traits under the reserved key, so the scorers apply the absolute gates.
  const traits = combined_trait_answers(user_profile);
  const periods = _truthy(user_profile["life_periods"]) ? user_profile["life_periods"] : {};
  const timezone_offset = _py_float(user_profile["birth"]["timezone_offset"]);
  const life_events: LifeEvent[] = [];
  for (const event of events) {
    try {
      life_events.push(event_payload_to_life_event(event));
    } catch (exc) {
      if (exc instanceof TypeError) throw exc;
      continue;
    }
  }

  // Multi-day (induced-delivery) windows: the ascendant cycle repeats every ~24h, so the rising
  // sign itself barely discriminates the DAY — the dated events/dasha do. We therefore sweep the
  // FULL multi-day span in absolute seconds, scoring each sampled minute on its ACTUAL calendar day
  // (so events land on the right dasha), but still bucket by rising sign on a 24h time-of-day axis.
  // Each sign keeps the best-scoring (day, time) across the whole window; `best_time` is then a
  // date-qualified key carrying the winning day, so committing a sign narrows to that day's window.
  const multiday = is_multiday_search(search);
  const sweep_start = multiday ? candidate_seconds(time_label_to_tuple(search["start"])) : 0;
  const sweep_end = multiday ? sweep_start + search_span_seconds(search) : 0;
  const [sd_start, sd_end] = multiday ? [sweep_start, sweep_end] : _window_second_range(search);
  const win_start_date: PlainDate | null = multiday ? search_start_date(search) : null;
  const step = Math.max(60, Math.trunc(step_minutes) * 60);
  const by_sign: Record<string, Record<string, any>> = {};
  // For a multi-day window, ALSO emit a band per CONTIGUOUS rising run so the Level-0 strip can lay
  // the days out left-to-right. Runs (not per-day min/max) are essential: the sign rising AT midnight
  // appears at BOTH ends of a calendar day, so a per-day min/max would span the whole day and draw a
  // single bar over everything. A contiguous run instead yields one clean band per actual rising
  // window (a midnight-crossing window is one band that spans the boundary).
  // `by_sign` stays the 12-sign aggregate the leaders/scores/derived panels consume unchanged.
  const day_runs: Record<string, any>[] = [];
  let current_run: Record<string, any> | null = null;
  const event_labels: string[] = [];
  let second = sd_start;
  while (second <= sd_end) {
    const wrapped = ((second % (24 * 3600)) + 24 * 3600) % (24 * 3600);
    const candidate: CandidateTuple = seconds_to_candidate(wrapped);
    // On a multi-day sweep, pin the candidate's real calendar day so the dated-event/dasha score is
    // correct; single-day/overnight leaves it null and resolves the day from the window as before.
    const candidate_date: PlainDate | null =
      multiday && win_start_date ? addDays(win_start_date, Math.floor(second / (24 * 3600))) : null;
    const record = score_candidate_record(
      people_key, candidate, birth_data, life_events, events, timezone_offset, {},
      "rising_sign_breakdown", null, search, traits, periods, null, candidate_date,
    );
    const sign = record["ascendant_sign"];
    if (sign === null || sign === undefined) {
      second += step;
      continue;
    }
    if (!(sign in by_sign)) {
      by_sign[sign] = {
        sign: sign, marks: [], events: {}, best_combined: null, best_time: null,
        best_event_fit: null, best_era_fit: null,
      };
    }
    const entry = by_sign[sign];
    entry["marks"].push(wrapped);
    for (const detail of record["kp_details"] ?? []) {
      const label = String(
        _truthy(detail["label"]) ? detail["label"]
          : _truthy(detail["category"]) ? detail["category"]
            : _truthy(detail["date"]) ? detail["date"]
              : "Event",
      );
      const score = detail["score"];
      if (score === null || score === undefined) {
        continue;
      }
      if (!event_labels.includes(label)) {
        event_labels.push(label);
      }
      if (score > (entry["events"][label] ?? -Infinity)) {
        entry["events"][label] = pythonRound(Number(score), 1);
      }
    }
    // Pure event-fit (no appearance bias) — the per-sign EVENT confidence, so the user can see
    // which sign the dated events actually support vs which appearance resembles.
    const event_fit = record["event_fit_score"];
    if (
      event_fit !== null && event_fit !== undefined &&
      (entry["best_event_fit"] === null || event_fit > entry["best_event_fit"])
    ) {
      entry["best_event_fit"] = pythonRound(Number(event_fit), 1);
      // "Explained events" is read AT the sign's best event-fit moment (one fixed time — never a
      // per-fold re-search); the record itself carries it (score_candidate_record), so this is the
      // same definition the drill-down and Compare page show.
      entry["explained_fit"] = record["explained_fit"] ?? null;
      entry["explained_count"] = record["explained_count"] ?? null;
      entry["events_total"] = record["events_total"] ?? null;
    }
    const combined = record["deterministic_score"];
    if (
      combined !== null && combined !== undefined &&
      (entry["best_combined"] === null || combined > entry["best_combined"])
    ) {
      entry["best_combined"] = combined;
      entry["best_time"] = record["time"];
      // The Moon sign at this window's best moment — Personality is scored against it (Vol_06 §3.1).
      entry["moon_sign"] = record["moon_sign"] ?? null;
    }
    // Multi-day strip: extend the current contiguous rising run, or start a new one when the sign
    // changes. `second` is absolute seconds from the start date's midnight, so abs_start/abs_end give
    // the run's exact linear position; the run's `date` is the day it STARTS on (what commit() picks).
    if (multiday && candidate_date) {
      if (current_run && current_run["sign"] === sign) {
        current_run["abs_end"] = second;
      } else {
        if (current_run) day_runs.push(current_run);
        current_run = {
          sign, date: formatISODate(candidate_date), day_index: Math.floor(second / (24 * 3600)),
          abs_start: second, abs_end: second,
          best_event_fit: null, best_combined: null, best_time: null, moon_sign: null,
        };
      }
      if (event_fit !== null && event_fit !== undefined && (current_run["best_event_fit"] === null || event_fit > current_run["best_event_fit"])) {
        current_run["best_event_fit"] = pythonRound(Number(event_fit), 1);
        current_run["explained_fit"] = record["explained_fit"] ?? null;
        current_run["explained_count"] = record["explained_count"] ?? null;
        current_run["events_total"] = record["events_total"] ?? null;
      }
      if (combined !== null && combined !== undefined && (current_run["best_combined"] === null || combined > current_run["best_combined"])) {
        current_run["best_combined"] = combined;
        current_run["best_time"] = record["time"];
        current_run["moon_sign"] = record["moon_sign"] ?? null;
      }
    }
    second += step;
  }
  if (current_run) day_runs.push(current_run);

  // Appearance match: the AI's estimate when provided, otherwise the deterministic fallback.
  const appearance: Record<string, number> = _truthy(appearance_likelihood)
    ? { ...appearance_likelihood! }
    : Object.fromEntries(rising_sign_from_traits(traits));
  for (const [sign, entry] of Object.entries(by_sign)) {
    entry["appearance_match"] = sign in appearance ? pythonRound(appearance[sign], 1) : null;
    // Personality: the MODERN-astrology personality match against THIS window's Moon sign. WEAK,
    // Barnum-prone evidence. Identical across signs on a steady-Moon day; varies only where the
    // Moon changes sign — so it narrows the time but never decides the rising sign.
    entry["personality_match"] = personality_match_for_sign(traits, entry["moon_sign"]);
    const marks: number[] = entry["marks"] ?? [];
    delete entry["marks"];
    const arc = _circular_arc(marks);
    if (arc !== null) {
      entry["window_start"] = _seconds_to_label(arc[0]);
      entry["window_end"] = _seconds_to_label(arc[1]);
      entry["_sort_seconds"] = arc[0];
    } else {
      entry["window_start"] = entry["window_end"] = entry["best_time"];
      entry["_sort_seconds"] = 0;
    }
  }

  // Re-rank the per-sign Events fit by the placidus net significator (see ENSEMBLE_LENS_WEIGHTS —
  // whole-sign and divisional lenses are computed for audit but weighted 0, as they don't carry
  // stable sign-level signal). The band stays anchored at the mean evidence level so weak/ambiguous
  // evidence still reads low/grey. No-op in a narrow, already-committed window.
  try {
    const birth = _truthy(user_profile["birth"]) ? user_profile["birth"] : {};
    if (_truthy(events) && "date" in birth) {
      const bd = parseISODate(birth["date"]);
      _spread_event_fit_by_ensemble(
        Object.values(by_sign),
        { year: bd.year, month: bd.month, day: bd.day },
        _py_float(birth["lat"]),
        _py_float(birth["lon"]),
        timezone_offset,
        events,
        Boolean(birth["timezone_offset_fixed"]),
      );
    }
  } catch {
    // leave the placidus fits in place if the ensemble can't be computed
  }

  // SEPARATE "Eras" metric — the answered life-phase pseudo-events scored as their own per-sign band
  // (valence-aware), anchored near the events' level so the two bands compare. No-op without answers.
  try {
    const birth = _truthy(user_profile["birth"]) ? user_profile["birth"] : {};
    if (era_events.length && "date" in birth) {
      const bd = parseISODate(birth["date"]);
      const ev_fits = Object.values(by_sign).map((e) => e["best_event_fit"]).filter((v): v is number => v != null);
      const anchor = ev_fits.length ? ev_fits.reduce((a, b) => a + b, 0) / ev_fits.length : undefined;
      _spread_era_fit(
        Object.values(by_sign),
        { year: bd.year, month: bd.month, day: bd.day },
        _py_float(birth["lat"]), _py_float(birth["lon"]), timezone_offset,
        era_events, Boolean(birth["timezone_offset_fixed"]), anchor,
      );
    }
  } catch {
    // no era band if it can't be computed
  }

  // RECORDED-TIME PRIOR at the SIGN level (owner re-scope 2026-07-02 — src/lib/time_prior.ts):
  // a `recorded`/`approximate` entered clock discounts each sign's Events fit by the factor at
  // its window's CLOSEST APPROACH to that clock (1.0 when the clock falls inside the window;
  // bounded 0.8/0.9 floors — a decisive events lead can still overturn a mis-remembered clock).
  // No-op for range/unknown certainties and for profiles with no entered time (all blind
  // benchmarks unchanged). Applied AFTER the ensemble + era spreads so their anchoring stays on
  // the raw chart evidence; the raw fit is kept for audit.
  try {
    const birth = _truthy(user_profile["birth"]) ? user_profile["birth"] : {};
    const entered = parse_time_seconds(birth["entered_time"]);
    const certainty = birth["time_certainty"];
    if (entered !== null && TIME_PRIOR_TIERS[String(certainty ?? "").trim().toLowerCase()]) {
      for (const entry of Object.values(by_sign)) {
        if (entry["best_event_fit"] === null || entry["best_event_fit"] === undefined) continue;
        if (!entry["window_start"] || !entry["window_end"]) continue;
        const start = candidate_seconds(time_label_to_tuple(entry["window_start"]));
        const end = candidate_seconds(time_label_to_tuple(entry["window_end"]));
        const factor = time_prior_for_window(certainty, entered, start, end);
        if (factor < 1.0) {
          entry["best_event_fit_unprior"] = entry["best_event_fit"]; // audit: the raw evidence fit
          entry["best_event_fit"] = pythonRound(Number(entry["best_event_fit"]) * factor, 1);
        }
        entry["time_prior"] = pythonRound(factor, 4);
      }
    }
  } catch {
    // prior is a bounded refinement — never let it break the breakdown
  }

  // Ascending by the time the sign rises — chronological through the day.
  const ordered = Object.values(by_sign).sort((a, b) => a["_sort_seconds"] - b["_sort_seconds"]);
  for (const entry of ordered) {
    delete entry["_sort_seconds"];
  }

  // Multi-day strip: SignRow-compatible per-(day, sign) bands. Events/best_time/abs span are
  // PER-DAY (so the days read differently); appearance/personality/eras are sign-level (the
  // ascendant pattern repeats each day), copied from the aggregated sign so the colours/tooltips
  // stay consistent. Ordered chronologically across the whole multi-day axis.
  let day_bands: Record<string, any>[] | undefined;
  if (multiday) {
    const agg: Record<string, Record<string, any>> = {};
    for (const e of ordered) agg[e["sign"]] = e;
    day_bands = day_runs
      .sort((a, b) => a["abs_start"] - b["abs_start"])
      .map((db) => {
        const a = agg[db["sign"]] ?? {};
        // LEVEL-0 CONSISTENCY: the per-sign aggregates above were re-scaled by the ensemble
        // spread and the recorded-time prior, but each day-run still carries the RAW per-run
        // event fit — the two Level-0 surfaces would contradict (a day band higher/lower than
        // its own sign's number). CHOICE: shift each band by its sign's (final − raw) delta
        // rather than overwriting with the sign aggregate — a monotone per-sign shift keeps the
        // per-DAY ordering visible while the sign's best day lands exactly on the sign's final
        // aggregate value (the raw sign fit IS the max over its day runs).
        const sign_final = a["best_event_fit"] ?? null;
        const sign_raw = a["best_event_fit_placidus"] ?? a["best_event_fit_unprior"] ?? sign_final;
        let band_fit = db["best_event_fit"] ?? null;
        if (band_fit !== null && sign_final !== null && sign_raw !== null) {
          band_fit = pythonRound(
            Math.max(0, Math.min(100, Number(band_fit) + (Number(sign_final) - Number(sign_raw)))), 1,
          );
        }
        return {
          sign: db["sign"],
          date: db["date"],
          day_index: db["day_index"],
          abs_start: db["abs_start"],
          abs_end: db["abs_end"],
          window_start: _seconds_to_label(((db["abs_start"] % 86400) + 86400) % 86400),
          window_end: _seconds_to_label(((db["abs_end"] % 86400) + 86400) % 86400),
          best_time: db["best_time"],
          best_event_fit: band_fit,
          explained_fit: db["explained_fit"] ?? null,
          explained_count: db["explained_count"] ?? null,
          events_total: db["events_total"] ?? null,
          best_era_fit: a["best_era_fit"] ?? null,
          appearance_match: a["appearance_match"] ?? null,
          personality_match: personality_match_for_sign(traits, db["moon_sign"]),
          moon_sign: db["moon_sign"] ?? null,
          events: a["events"] ?? {},
        };
      });
  }

  return {
    available: true,
    event_labels: event_labels,
    signs: ordered,
    multiday,
    day_bands,
    traits: per_question_sign_match(traits),
    has_appearance: _truthy(appearance),
    dasha_concentration: events_dasha_concentration(user_profile),
  };
}

/**
 * The rising signs that actually occur within the search window, in the order they first
 * appear. Used to offer only astronomically-possible signs when picking the rising sign.
 */
export function signs_in_range(user_profile: Record<string, any>, step_minutes = 3): string[] {
  const search = _truthy(user_profile["search"]) ? user_profile["search"] : {};
  if (!(_truthy(search["start"]) && _truthy(search["end"]))) {
    return [];
  }
  const birth = _truthy(user_profile["birth"]) ? user_profile["birth"] : {};
  let birth_date;
  let lat: number;
  let lon: number;
  let tz: number;
  try {
    if (!("date" in birth)) throw new Error("KeyError: 'date'");
    birth_date = parseISODate(birth["date"]);
    [lat, lon, tz] = [_py_float(birth["lat"]), _py_float(birth["lon"]), _py_float(birth["timezone_offset"])];
  } catch {
    return [];
  }
  const cfg = get_config();
  let [start_s, end_s] = _window_second_range(search);
  if (is_multiday_search(search)) {
    // A multi-day window (span > 24h) contains EVERY clock time at least once, but
    // `_window_second_range` reads only the start/end clocks and would collapse e.g.
    // 00:00 → 00:00-over-3-days to a single probe. Sweep the whole 24h circle instead.
    [start_s, end_s] = [0, 24 * 3600 - 1];
  }
  const step = Math.max(60, Math.trunc(step_minutes) * 60);
  const found: string[] = [];
  let second = start_s;
  while (second <= end_s) {
    const wrapped = second % (24 * 3600);
    const probe_dt = plainDateTime(
      birth_date.year, birth_date.month, birth_date.day,
      Math.floor(wrapped / 3600), Math.floor((wrapped % 3600) / 60), wrapped % 60,
    );
    const tz_at = birth_offset_at(
      { lat, lon, timezone_offset: tz, timezone_offset_fixed: birth["timezone_offset_fixed"] }, probe_dt, tz,
    );
    const chart = calculate_chart(probe_dt, lat, lon, tz_at, cfg);
    const sign = chart.planets["ascendant"].sign_name;
    if (!found.includes(sign)) {
      found.push(sign);
    }
    second += step;
  }
  return found;
}

// A category times SHARPLY under Vimshottari if its reliability is at/above this — only these
// (marriage, childbirth, death) carry enough timing signal to pin a rising sign on their own.
export const SHARP_TIMING_THRESHOLD = 0.75;

/**
 * Explain WHEN dated events fundamentally can't separate the rising signs — so a user seeing
 * clustered, low per-sign accuracy (e.g. Zane: a 7-year-old with four soft events) understands it
 * is the evidence, not a bug, and that the recorded time + appearance are the stronger levers.
 *
 * Two structural causes are detected:
 *   • single dasha period — all events fall in one Mahadasha, so the dominant timing lever is
 *     constant and can't discriminate signs (common for children / events clustered in a few years);
 *   • no sharply-timed event — only soft categories (illness, moves, trips) are present, which
 *     corroborate a time but never pin the rising sign.
 *
 * The Mahadasha timeline is essentially birth-time-independent within a day (it is fixed by the
 * Moon's nakshatra), so a noon reference chart is used. Returns {n_events, n_sharp,
 * distinct_mahadashas, warnings:[...]}.
 */
export function events_discrimination_diagnostic(user_profile: Record<string, any>): Record<string, any> {
  const events = (_truthy(user_profile["events"]) ? user_profile["events"] : []).filter((e: any) =>
    _truthy(e["start_date"]),
  );
  const out: Record<string, any> = { n_events: events.length, n_sharp: 0, distinct_mahadashas: 0, warnings: [] };
  if (!events.length) {
    return out;
  }

  out["n_sharp"] = events.filter(
    (e: any) =>
      (CATEGORY_TIMING_RELIABILITY[String(_truthy(e["category"]) ? e["category"] : "").trim().toLowerCase()] ?? 0.6) >=
      SHARP_TIMING_THRESHOLD,
  ).length;

  const birth = _truthy(user_profile["birth"]) ? user_profile["birth"] : {};
  let bd;
  let lat: number;
  let lon: number;
  let tz: number;
  try {
    if (!("date" in birth)) throw new Error("KeyError: 'date'");
    bd = parseISODate(birth["date"]);
    [lat, lon, tz] = [_py_float(birth["lat"]), _py_float(birth["lon"]), _py_float(birth["timezone_offset"])];
  } catch {
    return out; // no chart → skip the dasha-span check, keep the sharp-event count
  }

  const ref_dt = plainDateTime(bd.year, bd.month, bd.day, 12, 0, 0);
  let dasha_init: Record<string, number>;
  try {
    // Birth-side offset via birth_offset_at (fixed/pinned pre-1970 offsets honoured), matching
    // the scoring engine's own conversions rather than the raw profile offset.
    const tz_at = birth_offset_at(
      { lat, lon, timezone_offset: tz, timezone_offset_fixed: birth["timezone_offset_fixed"] }, ref_dt, tz,
    );
    const chart = calculate_chart(ref_dt, lat, lon, tz_at, get_config());
    dasha_init = init_dasha_jd(chart, local_to_jd(ref_dt, tz_at));
  } catch {
    return out;
  }
  const mahadashas = new Set<string>();
  for (const event of events) {
    let event_dt;
    try {
      event_dt = parseISODateTime(event["start_date"]);
    } catch {
      continue;
    }
    if (diffSeconds(event_dt, ref_dt) <= 0) {
      continue;
    }
    let dasha;
    try {
      dasha = get_current_dasha(event_dt, ref_dt, dasha_init, tz);
    } catch {
      continue;
    }
    if (_truthy(dasha.mahadasha_lord)) {
      mahadashas.add(dasha.mahadasha_lord);
    }
  }
  out["distinct_mahadashas"] = mahadashas.size;

  if (events.length >= 2 && mahadashas.size === 1) {
    out["warnings"].push(
      "All your dated events fall within a SINGLE major dasha period, so they can't separate the " +
        "rising signs from one another — common when events are clustered in a few years (e.g. a young " +
        "child). Per-sign accuracy will look low and similar across signs; that's the evidence, not a " +
        "bug. Lean on the recorded time and appearance here.",
    );
  }
  if (out["n_sharp"] === 0) {
    out["warnings"].push(
      "None of your events is a sharply-timed type (marriage, childbirth, a death). Softer events " +
        "(illness, surgery, moves, trips) corroborate a time but can't pin the rising sign on their own, " +
        "so per-sign accuracy stays low and clustered.",
    );
  }
  return out;
}

/** Convenience: scan + verdict in one call. */
export function rising_sign_confidence(
  user_profile: Record<string, any>,
  step_minutes: number = DEFAULT_STEP_MINUTES,
): Record<string, any> {
  const per_sign = scan_rising_signs(user_profile, step_minutes);
  // Only dated events that can actually be scored count toward evidence sufficiency.
  const event_count = (_truthy(user_profile["events"]) ? user_profile["events"] : []).filter((e: any) =>
    _truthy(e["start_date"]),
  ).length;
  const verdict = confidence_verdict(per_sign, event_count);
  verdict["per_sign"] = per_sign;
  return verdict;
}
